import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { CheckCircle2, Download, MessageCircle, Rocket, Zap } from 'lucide-react';
import LandingButton from './LandingButton';
import SpecularButton from './SpecularButton';
import VariableProximity from './VariableProximity';
import LandingContainer from './LandingContainer';
import CountUp from './CountUp';
import CursorGrid from './CursorGrid';
import { LIVE_STATS } from './liveStats';
import { SIGNUP_URL } from './urls';
import { useT, useLang, downloadPathFor } from './i18n';


const wordVariants = {
  hidden: { opacity: 0, y: 26, filter: 'blur(6px)' },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.55, delay: 0.12 + i * 0.07, ease: [0.22, 1, 0.36, 1] },
  }),
};

// Plávajúca sklenená mini-karta pri mockupe
const FloatCard = ({ className, delay, icon, title, sub }) => (
  <motion.div
    className={`landing-float-card ${className}`}
    initial={{ opacity: 0, y: 24, scale: 0.9 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    <span className="landing-float-card__icon">{icon}</span>
    <span>
      <span className="landing-float-card__title">{title}</span>
      <span className="landing-float-card__sub">{sub}</span>
    </span>
  </motion.div>
);

const LandingHero = () => {
  const t = useT();
  const lang = useLang();
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);
  const headlineRef = useRef(null);
  // Kaskáda po významových celkoch — každý jazyk si delí riadky sám.
  const headlineLine1 = t('hero.headlineLine1');
  const headlineLine2 = t('hero.headlineLine2');
  const headlineAccent = t('hero.headlineAccent');
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const shotY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 50]);

  return (
    <section ref={sectionRef} className="landing-hero-dark">
      {/* Aurora pozadie + jemná mriežka */}
      <div className="landing-hero-dark__aurora" aria-hidden />
      <div className="landing-hero-dark__grid" aria-hidden />
      {/* Interaktívna mriežka — rozsvecuje bunky pod kurzorom, pulz na klik */}
      {!reduce && (
        <div className="landing-hero-dark__cursorgrid" aria-hidden>
          <CursorGrid
            cellSize={44}
            color="#2563EB"
            radius={130}
            falloff="smooth"
            holdTime={350}
            fadeDuration={900}
            lineWidth={1}
            maxOpacity={0.32}
            fillOpacity={0.05}
            gridOpacity={0}
            clickPulse
            pulseSpeed={650}
          />
        </div>
      )}

      <LandingContainer className="landing-hero-dark__inner">
        <div className="landing-hero-grid">
          <motion.div initial="hidden" animate="visible">
            <h1 ref={headlineRef} className="landing-h1 landing-h1--dark landing-h1--proximity">
              <motion.span className="landing-hero-line" variants={wordVariants} custom={1}>
                <VariableProximity
                  label={headlineLine1.join(' ')}
                  containerRef={headlineRef}
                  fromFontVariationSettings="'wght' 700, 'opsz' 24"
                  toFontVariationSettings="'wght' 900, 'opsz' 32"
                  radius={130}
                  falloff="gaussian"
                />
              </motion.span>
              <motion.span className="landing-hero-line" variants={wordVariants} custom={2}>
                <VariableProximity
                  label={headlineLine2.join(' ')}
                  containerRef={headlineRef}
                  fromFontVariationSettings="'wght' 700, 'opsz' 24"
                  toFontVariationSettings="'wght' 900, 'opsz' 32"
                  radius={130}
                  falloff="gaussian"
                />
              </motion.span>
              <motion.span
                className="landing-hero-line landing-hero-word--accent"
                variants={wordVariants}
                custom={3}
                style={{ whiteSpace: 'nowrap' }}
              >
                <VariableProximity
                  label={headlineAccent.join(' ')}
                  containerRef={headlineRef}
                  fromFontVariationSettings="'wght' 700, 'opsz' 24"
                  toFontVariationSettings="'wght' 900, 'opsz' 32"
                  radius={130}
                  falloff="gaussian"
                />
              </motion.span>
            </h1>

            <motion.p className="landing-lead landing-lead--dark" variants={wordVariants} custom={8}>
              {t('hero.lead')}
            </motion.p>

            <motion.div className="landing-row" variants={wordVariants} custom={9}>
              <SpecularButton
                href={SIGNUP_URL}
                size="lg"
                className="specular-button--brand"
                radius={10}
                lineColor="#ffffff"
                baseColor="#1d4ed8"
                intensity={1.1}
                shineSize={12}
                shineFade={44}
                thickness={1.2}
                proximity={300}
                followMouse
              >
                <Rocket size={20} strokeWidth={2.2} aria-hidden />
                {t('hero.ctaPrimary')}
              </SpecularButton>
              <LandingButton size="lg" className="landing-btn--download" href={downloadPathFor(lang)}>
                <Download size={20} strokeWidth={2.2} aria-hidden />
                {t('hero.ctaDownload')}
              </LandingButton>
            </motion.div>

            <motion.p className="landing-hero-pricenote" variants={wordVariants} custom={10}>
              {t('hero.pricenote')}
            </motion.p>
          </motion.div>

          <div className="landing-hero-shot landing-hero-shot--stage">
            <motion.div className="landing-hero-shot__glow" style={{ y: glowY }} aria-hidden />
            <motion.div className="landing-hero-shot__device" style={{ y: shotY }}>
              <img
                src="/assets/market/hero-phone.webp"
                alt={t('hero.phoneAlt')}
                width={640}
                height={1191}
                className="landing-hero-phone"
              />
              {/* Karty kotvené na hrany telefónu, nie na stĺpec */}
              <FloatCard
                className="landing-float-card--tl"
                delay={0.7}
                icon={<CheckCircle2 size={18} strokeWidth={2.2} />}
                title={t('hero.card1Title')}
                sub={t('hero.card1Sub')}
              />
              <FloatCard
                className="landing-float-card--br"
                delay={0.9}
                icon={<MessageCircle size={18} strokeWidth={2.2} />}
                title={t('hero.card2Title')}
                sub={t('hero.card2Sub')}
              />
              <FloatCard
                className="landing-float-card--bl"
                delay={1.1}
                icon={<Zap size={18} strokeWidth={2.2} />}
                title={t('hero.card3Title')}
                sub={t('hero.card3Sub')}
              />
            </motion.div>
          </div>
        </div>

        {/* Živé čísla z produkcie */}
        <motion.div
          className="landing-stats"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          {LIVE_STATS.map((stat) => (
            <div key={stat.labelKey} className="landing-stats__item">
              <span className="landing-stats__value">
                <CountUp value={stat.value} suffix={stat.suffix} />
              </span>
              <span className="landing-stats__label">{t(stat.labelKey)}</span>
            </div>
          ))}
        </motion.div>
        <p className="landing-stats__note">{t('hero.statsNote')}</p>
      </LandingContainer>
    </section>
  );
};

export default LandingHero;
